import { Info } from 'lucide-react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ParameterDropdown } from '@/Domain/components/Action/Lexical/component/ParameterDropdown';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  form: UseFormReturn<HttpActionForm>;
};

const OPERATORS: { value: string; label: string }[] = [
  { value: 'is_truthy', label: 'is true' },
  { value: 'is_falsy', label: 'is false' },
  { value: 'exists', label: 'exists' },
  { value: 'not_exists', label: 'does not exist' },
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
];

const VALUE_OPERATORS = new Set(['equals', 'not_equals']);

/**
 * Editable list of run-conditions. The action runs only when every condition
 * holds for the current conversation metadata (evaluated server-side), so a
 * value the channel set or an earlier action captured can gate it. The
 * read-side twin of "Save to memory".
 */
export const RunConditions = ({ form }: Props) => {
  const { t } = useTranslation('actions_page');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'run_conditions',
  });

  const handleAdd = () =>
    append({ id: uuidv4(), metadata_key: '', operator: 'is_truthy', value: null });

  return (
    <div>
      <section className="relative grid h-16 grid-cols-[1fr_1fr_auto] items-center gap-2 border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
        <span>{t('sheet.form.run_conditions.table.metadata_key', 'Metadata key')}</span>
        <span className="flex items-center gap-1.5">
          {t('sheet.form.run_conditions.table.condition', 'Condition')}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="size-3.5 cursor-help text-foreground-subtle transition-colors hover:text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {t(
                  'sheet.form.run_conditions.hint',
                  'The action runs only when every condition holds for the current conversation. Evaluated on our side, so it cannot be bypassed from the chat.',
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <IconButton size="sm" onClick={handleAdd} data-testid="run-condition-add-button">
          <AddIcon />
        </IconButton>
      </section>

      {fields.length === 0 ? (
        <div className="mt-7 flex flex-col items-center justify-center gap-3">
          <p className="text-sm font-medium text-foreground">
            {t('sheet.form.run_conditions.empty.title', 'No run conditions')}
          </p>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            {t(
              'sheet.form.run_conditions.empty.description',
              'Add a condition to run this action only when a piece of conversation metadata holds, for example only when a flag set by your channel or an earlier action is true.',
            )}
          </p>
          <Button onClick={handleAdd} data-testid="run-condition-add-empty-button">
            <AddIcon />
            {t('sheet.form.run_conditions.add_button', 'Add condition')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {fields.map((field, index) => {
            const operator = form.watch(`run_conditions.${index}.operator`);
            return (
              <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] items-start gap-2 px-2">
                <FormField
                  name={`run_conditions.${index}.metadata_key`}
                  render={({ field: inputField, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <div className="flex items-center gap-1">
                          <div className="min-w-0 flex-1">
                            <Input
                              data-testid={`run-condition-key-${index}`}
                              placeholder={t(
                                'sheet.form.run_conditions.key_placeholder',
                                'metadata key',
                              )}
                              error={!!fieldState.error}
                              {...inputField}
                            />
                          </div>
                          <ParameterDropdown
                            onlyMetadataKeys
                            parameters={[]}
                            onSelect={(option) =>
                              inputField.onChange(option.name.replace(/^metadata_/, ''))
                            }
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col gap-2">
                  <Controller
                    control={form.control}
                    name={`run_conditions.${index}.operator`}
                    render={({ field: ctrl }) => (
                      <Select value={ctrl.value} onValueChange={ctrl.onChange}>
                        <SelectTrigger data-testid={`run-condition-operator-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map((op) => (
                            <SelectItem key={op.value} value={op.value}>
                              {t(`sheet.form.run_conditions.operator.${op.value}`, op.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {VALUE_OPERATORS.has(operator) && (
                    <FormField
                      name={`run_conditions.${index}.value`}
                      render={({ field: inputField }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              data-testid={`run-condition-value-${index}`}
                              placeholder={t(
                                'sheet.form.run_conditions.value_placeholder',
                                'value to compare',
                              )}
                              {...inputField}
                              value={inputField.value ?? ''}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                </div>
                <IconButton
                  size="sm"
                  variant="secondary"
                  onClick={() => remove(index)}
                  data-testid={`run-condition-remove-${index}`}
                  aria-label={t('sheet.form.run_conditions.remove_aria', 'Remove condition')}
                >
                  <TrashIcon />
                </IconButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
